import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { ReactNode } from 'react';
import { colors } from 'styles/colors';
import { pxToRem } from 'styles/pxToRem';
import { B2B_FIELD_ERROR_RESERVED_MIN_H } from '../B2bFieldLayout';

export type B2bYesNoToggleProps = {
  groupLabel: ReactNode;
  yesLabel: ReactNode;
  noLabel: ReactNode;
  value: boolean;
  onChange: (next: boolean) => void;
  isDisabled?: boolean;
  /** Shows the same required asterisk as {@link B2bFieldLayout} / `FieldText`. */
  isRequired?: boolean;
  error?: ReactNode;
  dir?: 'ltr' | 'rtl';
};

/**
 * RTL **Yes / No** control (lavender selected chip, white unselected) for organizer/event flags.
 */
export const B2bYesNoToggle = ({
  groupLabel,
  yesLabel,
  noLabel,
  value,
  onChange,
  isDisabled = false,
  isRequired = false,
  error,
  dir = 'rtl',
}: B2bYesNoToggleProps) => {
  return (
    <Box
      as='fieldset'
      border='none'
      p={0}
      m={0}
      w='full'
      dir={dir}
      aria-required={isRequired ? true : undefined}
    >
      <Box as='legend' display='block' w='full' float='none' p={0} mt={0} mx={0} mb={4}>
        <Flex
          as='span'
          display='inline-flex'
          align='baseline'
          justify='flex-start'
          flexWrap='wrap'
          columnGap={1.5}
          rowGap={0}
          w='full'
          verticalAlign='middle'
          fontFamily="'Noto Sans Arabic', 'Noto Sans', sans-serif"
          fontWeight='600'
          fontSize='md'
          lineHeight={1.4}
          color='dark.D800'
        >
          <Box as='span'>{groupLabel}</Box>
          {isRequired ? (
            <Text
              as='span'
              role='presentation'
              aria-hidden
              fontFamily="'Noto Sans Arabic', 'Noto Sans', sans-serif"
              fontWeight='600'
              fontStyle='normal'
              fontSize={pxToRem(12)}
              lineHeight={pxToRem(46.31)}
              letterSpacing='0'
              color={colors.red}
              sx={{ leadingTrim: 'none' }}
            >
              *
            </Text>
          ) : null}
        </Flex>
      </Box>
      <Flex dir={dir} gap={3} w='full' justify='flex-start' align='center' flexWrap='wrap'>
        <Button
          type='button'
          flexShrink={0}
          variant='unstyled'
          display='flex'
          alignItems='center'
          justifyContent='center'
          py={2.5}
          px={{ base: 6, lg: 8 }}
          minW={{ base: '4.5rem', lg: '5.5rem' }}
          borderRadius='xl'
          borderWidth='1px'
          borderStyle='solid'
          fontWeight='600'
          fontSize='md'
          fontFamily="'Noto Sans Arabic', 'Noto Sans', sans-serif"
          isDisabled={isDisabled}
          aria-pressed={value === true}
          onClick={() => onChange(true)}
          bg={value ? colors.b2b.landingFeatureIconBg : 'white'}
          borderColor={value ? colors.b2b.chipSelected : colors.b2b.chipInactiveBorder}
          color={value ? colors.b2b.chipSelected : 'dark.D800'}
          _hover={
            isDisabled
              ? undefined
              : {
                  borderColor: colors.b2b.chipSelected,
                  bg: value ? '#E4DDF2' : 'dark.D0',
                }
          }
          _focusVisible={{
            borderColor: colors.b2b.chipSelected,
            boxShadow: `0 0 0 1px ${colors.b2b.chipSelected}`,
          }}
        >
          {yesLabel}
        </Button>
        <Button
          type='button'
          flexShrink={0}
          variant='unstyled'
          display='flex'
          alignItems='center'
          justifyContent='center'
          py={2.5}
          px={{ base: 6, lg: 8 }}
          minW={{ base: '4.5rem', lg: '5.5rem' }}
          borderRadius='xl'
          borderWidth='1px'
          borderStyle='solid'
          fontWeight='600'
          fontSize='md'
          fontFamily="'Noto Sans Arabic', 'Noto Sans', sans-serif"
          isDisabled={isDisabled}
          aria-pressed={value === false}
          onClick={() => onChange(false)}
          bg={!value ? colors.b2b.landingFeatureIconBg : 'white'}
          borderColor={!value ? colors.b2b.chipSelected : colors.b2b.chipInactiveBorder}
          color={!value ? colors.b2b.chipSelected : 'dark.D800'}
          _hover={
            isDisabled
              ? undefined
              : {
                  borderColor: colors.b2b.chipSelected,
                  bg: !value ? '#E4DDF2' : 'dark.D0',
                }
          }
          _focusVisible={{
            borderColor: colors.b2b.chipSelected,
            boxShadow: `0 0 0 1px ${colors.b2b.chipSelected}`,
          }}
        >
          {noLabel}
        </Button>
      </Flex>
      <Box minH={B2B_FIELD_ERROR_RESERVED_MIN_H} mt={1} w='full'>
        {error != null ? (
          <Text textStyle='bodySmallRegular' color={colors.red} role='alert'>
            {error}
          </Text>
        ) : null}
      </Box>
    </Box>
  );
};
